import { useCallback } from 'react';
import PropTypes from 'prop-types';

import { DocumentScoreContainer } from 'src/components/DocumentScoreContainer';
import { DocumentScorePanel } from 'src/components/DocumentScorePanel';
import { LockedScoreButtons } from 'src/components/LockedScoreButtons';
import { Score } from 'src/components/Score';

import { useStyles } from './useStyles';

export const DocumentScoreBlock = ({
  className,
  variation,
  boundaries,
  onLockedGetScoreClick,
  onLockedUndoClick,
  onDetailsClick,
  onHover,
  loading,
  disabled,
  expanded,
  isLastBlockScore,
  displayLockedActions,
}) => {
  const styles = useStyles();

  const handleLockedGetScoreClick = useCallback(
    (element) => {
      onLockedGetScoreClick({ variation, element });
    },
    [onLockedGetScoreClick, variation]
  );

  const handleLockedUndoClick = useCallback(() => {
    onLockedUndoClick(variation);
  }, [onLockedUndoClick, variation]);

  const handleDetailsClick = useCallback(() => {
    onDetailsClick(variation);
  }, [onDetailsClick, variation]);

  const handleHover = useCallback(() => {
    onHover(variation);
  }, [onHover, variation]);

  return (
    <DocumentScoreContainer
      className={className}
      boundaries={boundaries}
      locked={variation.isScoreLocked || variation.performancePredictionsScoreLocked}
    >
      {variation.isScoreLocked || variation.performancePredictionsScoreLocked ? (
        displayLockedActions ? (
          <LockedScoreButtons
            className={styles.lockedButtons}
            onGetScoreClick={handleLockedGetScoreClick}
            onUndoClick={handleLockedUndoClick}
            disabled={disabled}
            vertical
          />
        ) : (
          <Score locked />
        )
      ) : (
        <DocumentScorePanel
          instanceId={variation.instanceId}
          generationTool={variation.generationTool}
          assetType={variation.assetType}
          text={variation.text}
          language={variation.language}
          score={variation.score}
          onDetailsClick={handleDetailsClick}
          onHover={handleHover}
          expanded={expanded}
          loading={loading}
          isLastBlockScore={isLastBlockScore}
          variation={variation}
        />
      )}
    </DocumentScoreContainer>
  );
};

DocumentScoreBlock.propTypes = {
  className: PropTypes.string,
  variation: PropTypes.shape({
    id: PropTypes.string,
    instanceId: PropTypes.string,
    generationTool: PropTypes.string,
    assetType: PropTypes.string,
    text: PropTypes.string,
    language: PropTypes.string,
    score: PropTypes.number,
    isScoreLocked: PropTypes.bool,
    isSaved: PropTypes.bool,
    performancePredictionsScoreLocked: PropTypes.bool,
  }).isRequired,
  boundaries: PropTypes.shape({}).isRequired,
  onLockedGetScoreClick: PropTypes.func,
  onLockedUndoClick: PropTypes.func,
  onDetailsClick: PropTypes.func.isRequired,
  onHover: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  expanded: PropTypes.bool,
  isLastBlockScore: PropTypes.bool,
  displayLockedActions: PropTypes.bool,
};

DocumentScoreBlock.defaultProps = {
  className: '',
  onLockedGetScoreClick: null,
  onLockedUndoClick: null,
  loading: false,
  disabled: false,
  expanded: false,
  isLastBlockScore: false,
  displayLockedActions: false,
};
